import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { wardrobeAPI, outfitAPI } from '../api';

function TryOnPage() {
  const [outfits, setOutfits] = useState([]);
  const [items, setItems] = useState([]);
  const [selectedOutfit, setSelectedOutfit] = useState(null);
  const [personImage, setPersonImage] = useState(null);
  const [personPreview, setPersonPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [showModal, setShowModal] = useState(false);
  
  // Outfit creation states
  const [isCreatingOutfit, setIsCreatingOutfit] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [showOutfitModal, setShowOutfitModal] = useState(false);
  const [outfitName, setOutfitName] = useState('');
  
  // Filter and pagination states
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [filterOccasion, setFilterOccasion] = useState('');
  const [filterSeason, setFilterSeason] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const ITEMS_PER_PAGE = 4; // 2x2 grid
  
  // Photo guidelines modal
  const [showPhotoGuidelinesModal, setShowPhotoGuidelinesModal] = useState(false);

  useEffect(() => {
    loadOutfits();
    loadItems();
  }, []);

  const loadOutfits = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await outfitAPI.getOutfits();
      console.log('Outfits response:', response.data);
      const outfitsData = Array.isArray(response.data) ? response.data : [];
      setOutfits(outfitsData);
    } catch (err) {
      console.error('Failed to load outfits:', err);
      const errorMsg = err.response?.data?.detail || 'Failed to load outfits';
      setError(typeof errorMsg === 'string' ? errorMsg : 'Failed to load outfits');
    } finally {
      setLoading(false);
    }
  };

  const loadItems = async () => {
    try {
      const response = await wardrobeAPI.getItems();
      const itemsData = Array.isArray(response.data) ? response.data : [];
      setItems(itemsData);
    } catch (err) {
      console.error('Failed to load items:', err);
    }
  };

  // Filter outfits
  const filteredOutfits = outfits.filter(outfit => {
    const matchesOccasion = !filterOccasion || outfit.occasion === filterOccasion;
    const matchesSeason = !filterSeason || (outfit.season && outfit.season.includes(filterSeason));
    return matchesOccasion && matchesSeason;
  });

  // Paginate filtered outfits
  const totalPages = Math.ceil(filteredOutfits.length / ITEMS_PER_PAGE);
  const paginatedOutfits = filteredOutfits.slice(
    currentPage * ITEMS_PER_PAGE,
    (currentPage + 1) * ITEMS_PER_PAGE
  );

  const handlePersonImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPersonImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPersonPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const toggleItemSelection = (itemId) => {
    setSelectedItems(prev => {
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId);
      } else {
        if (prev.length >= 3) {
          setError('Maximum 3 items per outfit');
          setTimeout(() => setError(''), 3000);
          return prev;
        }
        return [...prev, itemId];
      }
    });
  };

  const handleCreateOutfit = () => {
    if (selectedItems.length < 1) {
      setError('Please select at least 1 item to create an outfit');
      setTimeout(() => setError(''), 3000);
      return;
    }
    if (selectedItems.length > 3) {
      setError('Maximum 3 items allowed per outfit');
      setTimeout(() => setError(''), 3000);
      return;
    }
    setShowOutfitModal(true);
  };

  const handleSaveOutfit = async () => {
    try {
      setLoading(true);
      // Send empty strings for auto-generation by VL
      await outfitAPI.createOutfit(
        outfitName.trim() || '',  // Empty name will be generated by VL
        '',  // Description will be generated by VL
        selectedItems,
        '',  // Occasion will be generated by VL
        ''   // Season will be generated by VL
      );
      
      // Reset states
      setShowOutfitModal(false);
      setIsCreatingOutfit(false);
      setSelectedItems([]);
      setOutfitName('');
      
      // Reload outfits
      await loadOutfits();
      
      setError('');
      alert('✅ Outfit created successfully!');
    } catch (err) {
      console.error('Failed to create outfit:', err);
      setError(err.response?.data?.detail || 'Failed to create outfit');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelOutfitCreation = () => {
    setIsCreatingOutfit(false);
    setSelectedItems([]);
    setShowOutfitModal(false);
    setOutfitName('');
  };

  const handleTryOn = async () => {
    if (!selectedOutfit) {
      setError('Please select an outfit');
      return;
    }
    if (!personImage) {
      setError('Please upload your photo');
      return;
    }

    setProcessing(true);
    setError('');

    try {
      // Try on the complete outfit using outfit_id
      const response = await wardrobeAPI.tryOn(null, personImage, null, selectedOutfit.id);
      setResult(response.data);
      setShowModal(true);
      
      // Clear form
      setSelectedOutfit(null);
      setPersonImage(null);
      setPersonPreview(null);
      document.getElementById('person-input').value = '';
    } catch (err) {
      console.error('Try-on error:', err);
      let errorMsg = 'Virtual try-on failed. Please try again.';
      if (err.response?.data?.detail) {
        errorMsg = typeof err.response.data.detail === 'string' 
          ? err.response.data.detail 
          : JSON.stringify(err.response.data.detail);
      }
      setError(errorMsg);
    } finally {
      setProcessing(false);
    }
  };

  const navigate = useNavigate();

  return (
    <div 
      className="container" 
      style={{ 
        backgroundImage: `url(${process.env.PUBLIC_URL}/assets/branding/hero-fashion.png)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h1>Virtual Try-On</h1>
        <button 
          className="btn btn-secondary"
          onClick={() => navigate('/history')}
          style={{ fontSize: '0.9rem', padding: '0.5rem 1rem' }}
        >
          📸 View History
        </button>
      </div>
      
      {error && <div className="error-message">{error}</div>}
      
      {isCreatingOutfit ? (
        /* Outfit Creation Mode */
        <div className="card">
          <div className="header-top">
            <h3>Create New Outfit</h3>
            <div className="outfit-actions">
              <button 
                className="btn btn-success btn-small"
                onClick={handleCreateOutfit}
                disabled={selectedItems.length < 1 || selectedItems.length > 3}
              >
                Next ({selectedItems.length})
              </button>
              <button 
                className="btn btn-secondary btn-small"
                onClick={handleCancelOutfitCreation}
              >
                Cancel
              </button>
            </div>
          </div>
          
          <div className="selection-hint">
            Select 1-3 items to create an outfit ({selectedItems.length}/3)
          </div>

          {loading ? (
            <div className="loading-spinner" />
          ) : items.length === 0 ? (
            <p className="text-center">No items in wardrobe. Add items first!</p>
          ) : (
            <div className="grid">
              {items.map((item) => (
                <div 
                  key={item.id} 
                  className={`wardrobe-item ${selectedItems.includes(item.id) ? 'selected' : ''} selectable`}
                  onClick={() => toggleItemSelection(item.id)}
                >
                  {selectedItems.includes(item.id) && (
                    <div className="selection-badge">{selectedItems.indexOf(item.id) + 1}</div>
                  )}
                  <img src={item.extracted_cloth_url} alt={item.item_name} />
                  <div className="item-info">
                    <h4>{item.item_name}</h4>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        /* Try-On Mode */
        <div className="grid-2col">
          {/* Select Outfit */}
          <div className="card">
            <div className="header-top">
              <h3>Step 1: Select Outfit</h3>
              <button 
                className="btn btn-primary btn-small"
                onClick={() => setIsCreatingOutfit(true)}
                disabled={items.length < 2}
              >
                ✨ New Outfit
              </button>
            </div>
          
          {/* Filter Button */}
          <button 
            className="btn btn-secondary btn-small"
            onClick={() => setShowFilterModal(true)}
            style={{ marginTop: '0.5rem', width: '100%' }}
          >
            🔍 Filter Outfits {(filterOccasion || filterSeason) && '(Active)'}
          </button>

          {loading ? (
            <div className="loading-spinner" />
          ) : outfits.length === 0 ? (
            <div className="empty-state">
              <p>No outfits yet. Create outfits from your wardrobe!</p>
              <p className="text-muted">Go to Wardrobe → Create Outfit</p>
            </div>
          ) : filteredOutfits.length === 0 ? (
            <div className="empty-state">
              <p>No outfits match your filters</p>
              <button className="btn btn-secondary" onClick={() => { setFilterOccasion(''); setFilterSeason(''); setCurrentPage(0); }}>Clear Filters</button>
            </div>
          ) : (
            <>
              <div className="outfits-container" style={{ height: '400px', overflow: 'hidden', marginTop: '1rem' }}>
                <div className="outfits-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                  {paginatedOutfits.map((outfit) => (
                    <div
                      key={outfit.id}
                      className={`outfit-card ${selectedOutfit?.id === outfit.id ? 'selected' : ''}`}
                      onClick={() => setSelectedOutfit(outfit)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="outfit-items-preview">
                        {outfit.items.slice(0, 3).map((item, idx) => (
                          <img 
                            key={item.id} 
                            src={item.extracted_cloth_url} 
                            alt={item.item_name}
                            style={{ 
                              zIndex: 3 - idx,
                              marginLeft: idx > 0 ? '-20px' : '0'
                            }}
                          />
                        ))}
                        {outfit.items.length > 3 && (
                          <div className="more-items">+{outfit.items.length - 3}</div>
                        )}
                      </div>
                      <div className="outfit-info">
                        <h4 style={{ fontSize: '0.9rem', margin: '0.5rem 0' }}>{outfit.name}</h4>
                        <p className="outfit-meta" style={{ fontSize: '0.8rem', margin: 0 }}>
                          {outfit.items.length} items
                          {outfit.occasion && ` • ${outfit.occasion}`}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="pagination" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', marginTop: '1rem' }}>
                  <button 
                    className="btn btn-secondary btn-small"
                    onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                    disabled={currentPage === 0}
                  >
                    ← Previous
                  </button>
                  <span style={{ fontSize: '0.9rem', color: '#666' }}>
                    Page {currentPage + 1} of {totalPages}
                  </span>
                  <button 
                    className="btn btn-secondary btn-small"
                    onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
                    disabled={currentPage === totalPages - 1}
                  >
                    Next →
                  </button>
                </div>
              )}
            </>
          )}
        </div>
        
        {/* Upload Person Photo */}
        <div className="card">
          <h3>Step 2: Upload Your Photo</h3>
          
          <div className="upload-area">
            <input
              id="person-camera-input"
              type="file"
              accept="image/*"
              capture="user"
              onChange={handlePersonImageChange}
              style={{ display: 'none' }}
            />
            <input
              id="person-gallery-input"
              type="file"
              accept="image/*"
              onChange={handlePersonImageChange}
              style={{ display: 'none' }}
            />
            
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '10px' }}>
              <button 
                className="btn btn-secondary"
                onClick={() => {
                  setShowPhotoGuidelinesModal(true);
                }}
                disabled={processing}
              >
                📋 Photo Guidelines
              </button>
            </div>
            
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button 
                className="btn btn-secondary"
                onClick={() => document.getElementById('person-camera-input').click()}
                disabled={processing}
              >
                🤳 Take Selfie
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => document.getElementById('person-gallery-input').click()}
                disabled={processing}
              >
                🖼️ Choose from Gallery
              </button>
            </div>
            
            {personPreview && (
              <div className="preview-container mt-2">
                <img src={personPreview} alt="Preview" style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: '8px' }} />
              </div>
            )}
          </div>
          
          <button 
            className="btn btn-primary mt-2"
            onClick={handleTryOn}
            disabled={processing || !selectedOutfit || !personImage}
            style={{ width: '100%' }}
          >
            {processing ? 'Processing... (This may take a minute)' : 'Try On Outfit'}
          </button>
        </div>
      </div>
      )}

      {/* Outfit Creation Modal */}
      {showOutfitModal && (
        <div className="modal-overlay" onClick={() => setShowOutfitModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Create New Outfit</h3>
            
            <div className="form-group">
              <p style={{marginBottom: '20px', color: '#666'}}>
                Creating outfit with {selectedItems.length} item{selectedItems.length > 1 ? 's' : ''}.
              </p>
              <p style={{marginBottom: '20px', color: '#666', fontSize: '0.9em'}}>
                AI will automatically analyze and generate:
              </p>
              <ul style={{marginLeft: '20px', marginBottom: '20px', color: '#666', fontSize: '0.9em'}}>
                <li>✨ Outfit name</li>
                <li>📝 Description</li>
                <li>🎯 Suitable occasions</li>
                <li>🌤️ Season recommendations</li>
              </ul>
            </div>

            <div className="form-group">
              <label>Custom Name (Optional)</label>
              <input
                type="text"
                placeholder="Leave empty for AI-generated name"
                value={outfitName}
                onChange={(e) => setOutfitName(e.target.value)}
                className="input-field"
              />
            </div>

            <div className="modal-actions">
              <button 
                className="btn btn-secondary"
                onClick={handleCancelOutfitCreation}
                disabled={loading}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleSaveOutfit}
                disabled={loading}
              >
                {loading ? 'Creating... (This may take 30-60 seconds)' : 'Create Outfit'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Photo Guidelines Modal */}
      {showPhotoGuidelinesModal && (
        <div className="modal-overlay" onClick={() => setShowPhotoGuidelinesModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <button className="modal-close" onClick={() => setShowPhotoGuidelinesModal(false)}>×</button>
            <h3>📸 Photo Guidelines for Best Results</h3>
            
            <div style={{ marginBottom: '1.5rem' }}>
              <p style={{ color: '#666', lineHeight: '1.6', marginBottom: '1rem' }}>
                For the best virtual try-on experience, please follow these guidelines:
              </p>
              
              <div style={{ background: '#f8f9fa', padding: '1rem', borderRadius: '8px', marginBottom: '1rem' }}>
                <h4 style={{ marginTop: 0, fontSize: '1rem', color: '#333' }}>✅ Do's:</h4>
                <ul style={{ marginBottom: 0, paddingLeft: '1.5rem', color: '#666' }}>
                  <li>Stand in good lighting (natural light is best)</li>
                  <li>Face the camera directly</li>
                  <li>Show your full upper body or full body</li>
                  <li>Stand against a simple, plain background</li>
                  <li>Wear fitted or neutral clothing</li>
                  <li>Keep arms slightly away from body</li>
                </ul>
              </div>

              <div style={{ background: '#fff3f3', padding: '1rem', borderRadius: '8px', marginBottom: '1rem' }}>
                <h4 style={{ marginTop: 0, fontSize: '1rem', color: '#333' }}>❌ Don'ts:</h4>
                <ul style={{ marginBottom: 0, paddingLeft: '1.5rem', color: '#666' }}>
                  <li>Avoid dim or harsh lighting</li>
                  <li>Don't wear bulky or oversized clothing</li>
                  <li>Avoid busy or cluttered backgrounds</li>
                  <li>Don't stand at an angle or turn sideways</li>
                  <li>Avoid covering your body with objects</li>
                </ul>
              </div>

              <div style={{ background: '#fff0f0', border: '2px solid #dc3545', padding: '1rem', borderRadius: '8px', marginBottom: '1.5rem' }}>
                <h4 style={{ marginTop: 0, fontSize: '1rem', color: '#dc3545', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  ⚠️ Important Policy
                </h4>
                <p style={{ marginBottom: 0, color: '#721c24', fontSize: '0.95rem', lineHeight: '1.6' }}>
                  <strong>Do NOT upload:</strong>
                </p>
                <ul style={{ marginBottom: '0.5rem', paddingLeft: '1.5rem', color: '#721c24' }}>
                  <li>Nude or inappropriate images</li>
                  <li>Images of children or minors</li>
                </ul>
                <p style={{ marginBottom: 0, color: '#721c24', fontSize: '0.9rem', fontWeight: '600' }}>
                  Violation of this policy will result in immediate account suspension and potential legal action.
                </p>
              </div>

              <h4 style={{ fontSize: '1rem', color: '#333', marginBottom: '1rem' }}>📷 Good Photo Examples:</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem', marginBottom: '1rem' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ borderRadius: '8px', overflow: 'hidden', marginBottom: '0.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                    <img 
                      src="/assets/examples/tryon-fullbody-example.png" 
                      alt="Full body example" 
                      style={{ width: '100%', height: 'auto', display: 'block' }}
                    />
                  </div>
                  <p style={{ fontSize: '0.8rem', color: '#666', margin: 0, fontWeight: '500' }}>Full body view</p>
                  <p style={{ fontSize: '0.75rem', color: '#999', margin: 0 }}>Clear, good lighting</p>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ borderRadius: '8px', overflow: 'hidden', marginBottom: '0.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                    <img 
                      src="/assets/examples/tryon-upperbody-example.png" 
                      alt="Upper body example" 
                      style={{ width: '100%', height: 'auto', display: 'block' }}
                    />
                  </div>
                  <p style={{ fontSize: '0.8rem', color: '#666', margin: 0, fontWeight: '500' }}>Upper body</p>
                  <p style={{ fontSize: '0.75rem', color: '#999', margin: 0 }}>Arms visible, plain background</p>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ borderRadius: '8px', overflow: 'hidden', marginBottom: '0.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                    <img 
                      src="/assets/examples/tryon-natural-pose-example.png" 
                      alt="Natural pose example" 
                      style={{ width: '100%', height: 'auto', display: 'block' }}
                    />
                  </div>
                  <p style={{ fontSize: '0.8rem', color: '#666', margin: 0, fontWeight: '500' }}>Natural pose</p>
                  <p style={{ fontSize: '0.75rem', color: '#999', margin: 0 }}>Good posture, well-lit</p>
                </div>
              </div>
            </div>

            <div className="modal-actions">
              <button 
                className="btn btn-secondary"
                onClick={() => setShowPhotoGuidelinesModal(false)}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={() => {
                  setShowPhotoGuidelinesModal(false);
                  document.getElementById('person-input').click();
                }}
              >
                📸 Continue to Upload
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filter Modal */}
      {showFilterModal && (
        <div className="modal-overlay" onClick={() => setShowFilterModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Filter Outfits</h3>
            
            <div className="form-group">
              <label>Occasion</label>
              <select 
                value={filterOccasion} 
                onChange={(e) => { setFilterOccasion(e.target.value); setCurrentPage(0); }}
                className="filter-select"
                style={{ width: '100%', padding: '0.5rem', borderRadius: '8px', border: '1px solid #ddd' }}
              >
                <option value="">All Occasions</option>
                <option value="casual">Casual</option>
                <option value="work">Work</option>
                <option value="party">Party</option>
                <option value="formal">Formal</option>
                <option value="wedding">Wedding</option>
                <option value="date">Date</option>
                <option value="sport">Sport</option>
              </select>
            </div>

            <div className="form-group">
              <label>Season</label>
              <select 
                value={filterSeason} 
                onChange={(e) => { setFilterSeason(e.target.value); setCurrentPage(0); }}
                className="filter-select"
                style={{ width: '100%', padding: '0.5rem', borderRadius: '8px', border: '1px solid #ddd' }}
              >
                <option value="">All Seasons</option>
                <option value="Spring">Spring</option>
                <option value="Summer">Summer</option>
                <option value="Fall">Fall</option>
                <option value="Autumn">Autumn</option>
                <option value="Winter">Winter</option>
              </select>
            </div>

            <div className="modal-actions">
              <button 
                className="btn btn-secondary"
                onClick={() => { 
                  setFilterOccasion(''); 
                  setFilterSeason(''); 
                  setCurrentPage(0);
                  setShowFilterModal(false);
                }}
              >
                Clear Filters
              </button>
              <button 
                className="btn btn-primary"
                onClick={() => setShowFilterModal(false)}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Result Modal */}
      {showModal && result && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            <h2>Virtual Try-On Result</h2>
            
            <div className="result-container">
              <img src={result.result_url} alt="Try-on result" style={{ maxWidth: '100%', borderRadius: '8px' }} />
            </div>
            
            <div className="mt-2">
              <a 
                href={result.result_url} 
                download 
                className="btn btn-secondary"
                target="_blank"
                rel="noopener noreferrer"
              >
                Download Image
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TryOnPage;
